/**
 * DraftAutosaveLine — autosave status + "Salva ed esci" affordance rendered
 * under every wizard step while the session is bound (PR-ONB-RESUME-1B,
 * review fixes #4/#5).
 *
 * Renders nothing for anonymous/legacy users.
 *
 *   - The conflict banner keys on the PERSISTENT `conflictNotice` (not the
 *     transient technical status): it stays visible after the server reload
 *     until the user dismisses it or edits again.
 *   - "Salva ed esci" drains the whole save queue and navigates ONLY on a
 *     clean result; on failure it offers the explicit choice
 *     "Riprova" / "Esci senza salvare".
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { PillButton } from '@ab-platform/ui/pill-button';
import { useDraftAutosaveHandle } from '@/hooks/useDraftAutosave';
import { autosaveStatusLabel } from '@/lib/draftAutosave';
import {
  SAVE_AND_EXIT_SLOW_MS,
  saveAndExitButtonView,
  type SaveAndExitState,
} from '@/lib/onboardingUxViews';

export function DraftAutosaveLine(): JSX.Element | null {
  const navigate = useNavigate();
  // SHARED machine from the layout provider (PR-ONB-PRECHECK-3): the
  // SummaryPage submit flushes the same instance before reading the revision.
  const handle = useDraftAutosaveHandle();
  const [exitState, setExitState] = useState<SaveAndExitState>('idle');

  if (!handle || !handle.active) return null;
  const { state, flushNow, dismissConflictNotice } = handle;

  const label = autosaveStatusLabel(state);
  const exitView = saveAndExitButtonView(exitState);

  const handleSaveAndExit = async (): Promise<void> => {
    setExitState('flushing');
    // Fallback (PR-ONB-WIZARD-AREA-UX-1): if the flush takes too long, surface
    // an explicit "Esci comunque" escape so the button is never stuck
    // indefinitely. The flush keeps running; a clean result still navigates.
    const slowTimer = setTimeout(
      () => setExitState((s) => (s === 'flushing' ? 'slow' : s)),
      SAVE_AND_EXIT_SLOW_MS,
    );
    try {
      const clean = await flushNow();
      if (clean) {
        navigate('/area/stato');
        return;
      }
      // Never navigate on a dirty/failed flush (review fix #5): the user
      // chooses explicitly between retrying and leaving without the last edits.
      setExitState('failed');
    } finally {
      clearTimeout(slowTimer);
    }
  };

  return (
    <div className="mx-auto mt-3 grid w-full max-w-2xl gap-2 px-4">
      {state.conflictNotice ? (
        <Banner tone="warning" title="Richiesta aggiornata altrove">
          <div className="grid gap-2">
            <span>
              La richiesta è stata aggiornata in un'altra sessione. Abbiamo
              ricaricato l'ultima versione salvata: verifica i dati e riprendi
              da qui.
            </span>
            <div>
              <PillButton
                type="button"
                size="sm"
                variant="ghost"
                onClick={dismissConflictNotice}
              >
                Ho capito
              </PillButton>
            </div>
          </div>
        </Banner>
      ) : null}
      {exitView.showForceExit ? (
        <Banner
          tone="warning"
          title={
            exitState === 'slow'
              ? 'Salvataggio in corso'
              : 'Salvataggio non completato'
          }
        >
          <div className="grid gap-2">
            <span>
              {exitState === 'slow'
                ? 'Il salvataggio sta richiedendo più del previsto. Puoi attendere ancora oppure uscire comunque.'
                : 'Le ultime modifiche potrebbero non essere state salvate.'}
            </span>
            <div className="flex gap-2">
              {exitState === 'failed' ? (
                <PillButton
                  type="button"
                  size="sm"
                  onClick={() => void handleSaveAndExit()}
                >
                  Riprova
                </PillButton>
              ) : null}
              <PillButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => navigate('/area/stato')}
              >
                Esci comunque
              </PillButton>
            </div>
          </div>
        </Banner>
      ) : null}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span aria-live="polite">{label ?? 'Bozza salvata automaticamente'}</span>
        <PillButton
          type="button"
          size="sm"
          variant="ghost"
          disabled={exitView.disabled}
          onClick={() => void handleSaveAndExit()}
        >
          {exitView.label}
        </PillButton>
      </div>
    </div>
  );
}
